// src/features/links/photoUrl.ts
// IR-1047 — turn a target-preview GCS object path into a displayable thumbnail
// URL.
//
// The backend stores `target_preview.photo_url` as a bare GCS object path
// (e.g. "link_previews/2984_1781551558.jpg"), NOT a full URL — mirroring how
// user avatars are stored as `profile_image_url` object paths. Unlike the
// /api/ops/v1/users path (which pre-signs the URL server-side and returns a
// full https URL on the row), the links read model hands us the raw path, so
// the SPA is responsible for building the displayable URL.
//
// Consistent with every other image/upload flow in this SPA, that traffic is
// routed at `VITE_API_BASE_URL` + `/api/ops/v1/...`; the backend serves the
// object behind a preview-image route. We therefore prefix the path with the
// API base + the fleet-links preview-image route.
//
// ASSUMPTION (noted for review): the backend exposes
// `GET /api/ops/v1/fleet/links/preview-image/<object-path>` that streams the
// GCS object. If the deployed contract instead pre-signs the URL server-side
// (returning a full `https://…` value like the users path), this helper is a
// no-op for that case — an already-absolute URL is returned unchanged.

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * Build a displayable thumbnail URL from a target-preview `photo_url`.
 * Returns null when there is no path. Absolute URLs (http/https) and
 * already-rooted paths ("/...") are returned unchanged so the helper is safe if
 * the backend ever pre-signs the URL.
 */
export function targetPreviewPhotoUrl(photoPath: string | null | undefined): string | null {
  if (!photoPath) return null;
  if (/^https?:\/\//i.test(photoPath)) return photoPath;
  if (photoPath.startsWith("/")) return `${BASE}${photoPath}`;
  return `${BASE}/api/ops/v1/fleet/links/preview-image/${photoPath}`;
}
